<template>
  <div class="custom-form custom-form--contact">
    <div class="custom-form__header">
      <slot name="content-introduction" />
    </div>
    <form @submit.prevent="submit"
          novalidate autocomplete="off">

      <div class="columns">
        <div class="column" v-if="!fieldFirstName.isDisabled">
          <div class="fieldset">
            <field-input v-model="form.firstName"
                         :v="$v.form.firstName"
                         :mandatoryValue="fieldFirstNameMandatory"
                         :field="fieldFirstName" ref="firstName" />
          </div>
        </div>

        <div class="column" v-if="!fieldLastName.isDisabled">
          <div class="fieldset">
            <field-input v-model="form.lastName"
                         :v="$v.form.lastName"
                         :field="fieldLastName"
                         :mandatoryValue="fieldLastNameMandatory"
                         ref="lastName" />
          </div>
        </div>

        <div class="column" v-if="!fieldEmail.isDisabled">
          <div class="fieldset">
            <field-input v-model="form.email"
                         :v="$v.form.email"
                         :field="fieldEmail" />
          </div>
        </div>


        <div class="column" v-if="!fieldEmail.isDisabled">
          <div class="fieldset">
            <field-input v-model="form.confirmEmail"
                         :v="$v.form.confirmEmail"
                         :field="fieldConfirmEmail" />
          </div>
        </div>


        <date-birth :class="{ 'date-error': !dateError }"
                    :mandatoryValue="fieldBirthdateMandatory"
                    @birth="setBirthData"
                    ref="dateBirthVal"
                    :field-birth="fieldBirth"
                    :field-minage-length="ageConcern"
                    :start-validate="validateDate"
                    :legend-dob="legendDob"
                    @birthIsValid="setBirthValidation" />

        <div class="column" />
      </div>

      <div class="column" v-if="!fieldSkinType.isDisabled">
        <div class="fieldset">
          <Field-select v-model="form.skinType"
                        :field="fieldSkinType" />
        </div>
      </div>
      <div class="custom-form__checkbox-group">
        <div class="">
          <div role="group" aria-labelledby="legend-concerns" class="form-group">
            <h3 id="legend-concerns" class="form-group__heading is-4">{{fieldSkinConcerns.label}}</h3>
            <ul>
              <li class="form-group__field" v-for="(item, index) in fieldSkinConcerns.options">
                <div class="field field--checkbox">
                  <input type="checkbox" :id="item.id" :v="$v.form.skinConcern" :value="item.label" v-model="checkedNames" :name="item.id" class="input is-sr-only">
                  <span class="switch"></span><label :for="item.id" class="label"><span>{{item.label}}</span></label><!----><!---->
                </div>
              </li>

            </ul>
            <!---->
          </div>
        </div>
      </div>


      <div class="custom-form__checkbox-group" :class="{ 'disabled': !dateError }"
           v-for="(item, index) in fieldCheckboxes"
           :key="index">
        <field-checkbox v-if="item.required"
                        :value="form.gdpr[`checkbox-${item.gdprId}`]"
                        v-model="form.gdpr[`checkbox-${item.gdprId}`]"
                        :v="$v.form.gdpr[`checkbox-${item.gdprId}`]"
                        :field="item" />
        <field-checkbox v-else
                        :value="form.gdpr[`checkbox-${item.gdprId}`]"
                        v-model="form.gdpr[`checkbox-${item.gdprId}`]"
                        :field="item" />
      </div>

      <captcha ref="captcha"
               v-if="fieldCaptcha.isVisible"
               :captcha-id="fieldCaptcha.id"
               :captcha-sitekey="fieldCaptcha.siteKey"
               :explicitly-render="true"
               @captchaError="(value) => this.getCaptchaError(value)">
        <p class="field-help"
           v-if="hasErrorCapcha"
           :id="fieldCaptcha.idError">
          {{ fieldCaptcha.error }}
        </p>
      </captcha>

      <div class="custom-form__actions">
        <div>
          <template v-if="!serverResponse && serverError === null">
            <button
              type="submit"
              :class="[ctaClass, { 'disabled': $v.form.$invalid || !this.isBirthValid }]"
              :disabled="$v.form.$invalid || !this.isBirthValid">
              <span class="btn__wrapper">{{ template.ctaSubmit }}</span>
            </button>
          </template>

          <template v-else>
            <a :href="ctaLink" :class="ctaClass">
              <span class="btn__wrapper">{{ template.ctaSuccess }}</span>
            </a>
          </template>
        </div>
        <div role="alert">
          <p v-if="serverResponse !== null"
             class="custom-form__response"
             :class="{ 'error': !serverResponse }">{{ serverResponse ? template.succeed : template.failure.default }}</p>
          <p v-if="serverError !== null"
             class="custom-form__response error">{{ template.error }}</p>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { email, required, sameAs, maxLength } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate/lib/index';
import { tag } from '@Foundation/analyticsHandler';
import { postData } from '@Foundation/services';
import FieldCheckbox from '@Feature/CustomForms/fields/FieldCheckbox/code/Scripts/components/field-checkbox.vue';
import FieldInput from '@Feature/CustomForms/fields/FieldInput/code/Scripts/components/field-input.vue';
import FieldRadio from '@Feature/CustomForms/fields/FieldRadio/code/Scripts/components/field-radio.vue';
import FieldSelect from '@Feature/CustomForms/fields/FieldSelect/code/Scripts/components/field-select.vue';
import FieldTextarea from '@Feature/CustomForms/fields/FieldTextarea/code/Scripts/components/field-textarea.vue';
import Captcha from '@Feature/Captcha/code/Scripts/components/captcha.vue';
import dateBirth from '@Feature/CustomForms/dateOfBirth/code/Scripts/components/date-birth.vue';

export default {
  name: 'custom-form-newsletter',

  components: { FieldCheckbox, FieldInput, FieldRadio, FieldSelect, dateBirth, FieldTextarea, Captcha },

  directives: { tag },

  mixins: [validationMixin],

  props: {
    fieldEmail: { type: Object, required: true, default: undefined },
    fieldEmailLength: { type: Object, required: false, default: undefined },
    fieldNameMinLength: { type: Object, required: false, default: 2 },
    fieldNameMaxLength: { type: Object, required: false, default: 50 },
    ageConcern: { type: Number, required: false, default: 18 },
    fieldConfirmEmail: { type: Object, required: true, default: undefined },
    fieldFirstName: { type: Object, required: true, default: undefined },
    fieldLastName: { type: Object, required: true, default: undefined },
    fieldCheckboxes: { type: Array, required: true, default: undefined },
    fieldBirth: { type: Object, required: true, default: undefined },
    fieldSkinType: { type: Object, required: true, default: undefined },
    fieldSkinConcerns: { type: Object, required: true, default: undefined },
    fieldCountry: { type: Object, required: true, default: undefined },
    fieldCaptcha: { type: Object, required: false, default: undefined },
    fieldFirstNameMandatory: { type: String, required: false, default: undefined },
    fieldLastNameMandatory: { type: String, required: false, default: undefined },
    fieldBirthdateMandatory: { type: String, required: false, default: undefined },
    template: { type: Object, required: true, default: undefined },
    apiUrl: { type: String, required: true, default: undefined },
    ctaClass: { type: String, default: 'btn btn--primary' },
    legendDob: { type: Number, required: false },
  },

  data() {
    return {
      form: {
        email: '',
        emailLength: '',
        confirmEmail: '',
        firstName: '',
        lastName: '',
        gdpr: {},
        skinConcerns: '',
        birth: {
          day: '',
          month: '',
          year: ''
        }
      },
      checkedNames: [],
      serverResponse: null,
      serverError: null,
      hasErrorCapcha: false,
      updateFields: false,
      ctaLink: null,
      isBirthValid: true,
      validateDate: true,
      dateError: true,
      newFieldBirth: {},
      checkFirstName: false,
      checkLastName: false,
    };
  },

  /**
   * Vuelidate config
   * @see {@link https://github.com/monterail/vuelidate|GitHub}
   */
  validations() {
    const validationSettings = {
      form: {
        email: {
          required,
          maxLength: maxLength(parseInt(this.fieldEmailLength.label)),
          email,
        },
        confirmEmail: {
          required,
          maxLength: maxLength(parseInt(this.fieldEmailLength.label)),
          sameAsPassword: sameAs('email'),
        },
        firstName: {},
        lastName: {},
        birth: {
          day: {}, month: {}, year: {}
        },
        gdpr: {}
      }
    };

    for (let index = 0; index < this.fieldCheckboxes.length; index++) {
      if (this.fieldCheckboxes[index].required) {
        validationSettings.form.gdpr[`checkbox-${this.fieldCheckboxes[index].gdprId}`] = {
          required,
          sameAs: sameAs(() => true)
        };
      }
    }
    this.fieldLastName.isDisabled ? validationSettings.form.lastName = {} : false;
    this.fieldFirstName.isDisabled ? validationSettings.form.firstName = {} : false;
    this.fieldLastNameMandatory ? validationSettings.form.lastName.required = false : validationSettings.form.lastName.required = true;
    this.fieldFirstNameMandatory ? validationSettings.form.firstName.required = false : validationSettings.form.firstName.required = true;
    this.fieldBirthdateMandatory ? validationSettings.form.birth.day.required = false : validationSettings.form.birth.day.required = true;
    this.fieldBirthdateMandatory ? validationSettings.form.birth.month.required = false : validationSettings.form.birth.month.required = true;
    this.fieldBirthdateMandatory ? validationSettings.form.birth.year.required = false : validationSettings.form.birth.year.required = true;

    return validationSettings;
  },

  watch: {
    country(value) {
      this.getCountryData(value);
    }
  },
  created() {
    this.setCheckboxesToVueData();
    window.onCaptchaAPIReady = this.onCaptchaAPIReady;
  },
  mounted() {

    setTimeout(() => {
      this.PredefinedValues();
    }, 500);
  },
  computed: {


  },
  /* eslint-disable no-undef */
  methods: {
    setCheckboxesToVueData() {
      this.fieldCheckboxes.forEach((item) => {
        this.$set(this.form.gdpr, `checkbox-${item.gdprId}`, item.value);
      });
    },
    setBirthValidation(payload) {
      this.isBirthValid = payload;
    },
    setBirthData(payload) {
      this.form.birth[payload.type] = payload.value;
      if (this.form.birth.year) {
        const dateOfBirth = `${this.form.birth.year}-${parseInt(this.form.birth.month)}-${this.form.birth.day}`;
        const today = new Date(), birthDate = new Date(dateOfBirth), age = today.getFullYear() - birthDate.getFullYear();
        if (age < this.ageConcern) {
          this.$refs.dateBirthVal.v.day.error = true;
          this.$refs.dateBirthVal.v.month.error = true;
          this.$refs.dateBirthVal.v.year.error = true;
          this.dateError = false;
        } else {
          this.$refs.dateBirthVal.v.day.error = false;
          this.$refs.dateBirthVal.v.month.error = false;
          this.$refs.dateBirthVal.v.year.error = false;
          this.dateError = true;
          // console.info("Age "+age+" is allowed")
        }
      }
    },
    getCaptchaError(val) {
      this.hasErrorCapcha = val;
    },

    /** check if captcha required for validation before sending a request
     * @param {function} callback - function to call after validation
     */
    validateCapcha(callback) {
      if (this.fieldCaptcha.isVisible) {
        this.$refs.captcha.checkCaptcha();

        this.$nextTick(() => {
          /* istanbul ignore else */
          if (!this.hasErrorCapcha) {
            callback();
          }
        });
      } else {
        callback();
      }
    },
    PredefinedValues() {
      Object.keys(this.$props).forEach((item) => {
        switch (item) {
          case 'fieldGender':
            this.form.gender = this.$props[item].value || '';
            break;
          case 'fieldFirstName':
            this.form.firstName = this.$props[item].value || '';
            break;
          case 'fieldLastName':
            this.form.lastName = this.$props[item].value || '';
            break;
          case 'fieldBirth':
            this.form.birth.day = this.$props[item].day.value || '';
            this.form.birth.month = this.$props[item].month.value || '';
            this.form.birth.year = this.$props[item].year.value || '';
            break;
          default:
            break;
        }
      });

      if (this.fieldBirth.year.listValue != null) {
        const thisYear = parseInt(this.ageConcern) - (new Date().getFullYear() - this.fieldBirth.year.listValue[0]);
        this.newFieldBirth = this.fieldBirth.year.listValue.slice(thisYear);
        this.$refs.dateBirthVal.years = this.newFieldBirth;
      }
    },
    gatagging(email=''){
      window.dataLayer.push({
        event: "uaevent",
        ecommerce: "undefined",
        eventAction: "newsletter",
        eventCategory: "registration",
        eventLabel: "body",
        event_name: "newsletter_registration",
        email : email,
        referral: "body"
      });
    },
    sendRequest() {
      const skinConcerns = this.checkedNames.toString();
      this.form.skinConcerns = this.checkedNames.toString();
      const gdpr = this.fieldCheckboxes.map((item, index) => {
        return {
          gdrpSettingItemId: this.fieldCheckboxes[index].gdprId,
          isSelected: this.form.gdpr[`checkbox-${item.gdprId}`]
        };
      });

      postData(`${window.location.origin}/${this.apiUrl}`, { ...this.form, gdpr, skinConcerns })
      .then((response) => {
        this.serverResponse = response.status;
        /* istanbul ignore else */
        if(this.serverResponse){
          this.gatagging(this.form.email);
        }
        if (this.serverResponse && !!response.url) {
          this.ctaLink = response.url;
        }
      })
      .catch(() => {
        this.serverError = true;
      });
    },

    /**
     * @todo
     * @returns {undefined}
     */
    submit() {
      this.validateDate = false;
      this.$v.form.$touch();
      if (this.$v.form.$pending || this.$v.form.$error) {
        this.validateDate = true;
        return;
      } else {
        this.validateDate = true;
        this.validateCapcha(this.sendRequest);
      }
    },
  }
};
</script>

<style lang='scss' src="./custom-form-newsletter.scss"></style>
